import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysInvoice } from '../queryKeys';

import { type AdditionalNoteSuccessResponse } from './types';
import { zodSuccessResponseSchema } from './validations';

type Variables = { invoiceId: UUID; invoiceItemId: number };

/**
 * Deletes the additional note for an invoice item.
 *
 * @param invoiceId - The ID of the invoice.
 * @param invoiceItemId - The ID of the invoice item whose additional note should be deleted.
 * @returns The success response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/delete_invoice_item_additional_note_invoice__invoice_id__item__invoice_item_id__additional_note_delete
 */
export const deleteInvoiceItemAdditionalNote = async (
  invoiceId: UUID,
  invoiceItemId: number,
): Promise<AdditionalNoteSuccessResponse> => {
  const response = await vestigasApi
    .delete(
      ENDPOINT.INVOICE.DELETE_ITEM_ADDITIONAL_NOTE(invoiceId, invoiceItemId),
    )
    .json<AdditionalNoteSuccessResponse>();

  return zodSuccessResponseSchema.parse(response);
};

/**
 * React Query mutation hook for deleting an additional note from an invoice item.
 *
 * @param options - Optional TanStack Query mutation options.
 * @returns A mutation object to trigger the deletion.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/delete_invoice_item_additional_note_invoice__invoice_id__item__invoice_item_id__additional_note_delete
 */
export const useMutationDeleteInvoiceItemAdditionalNote = (
  options?: Omit<
    UseMutationOptions<AdditionalNoteSuccessResponse, Error, Variables>,
    'mutationFn' | 'onSettled'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, invoiceItemId }: Variables) =>
      deleteInvoiceItemAdditionalNote(invoiceId, invoiceItemId),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoice.get(variables.invoiceId),
      });
    },
    ...options,
  });
};
